import React from "react";
import { Button, Tooltip } from "antd";
import { CloseCircleOutlined, WarningOutlined } from "@ant-design/icons";
import "./CreditsBanner.css";
import BuyMore from "../../assets/icons/BuyMore.svg";
import UpgradePlanIcon from "../../assets/icons/UpgradePlan.svg";
import {
  CREDIT_ALERT_TYPE_CREDITS_EXHAUSTED,
  CREDIT_ALERT_TYPE_CREDITS_LOW_75,
  CREDIT_ALERT_TYPE_CREDITS_LOW_90,
  CREDIT_ALERT_TYPE_PILOT_EXPIRED,
  CREDIT_ALERT_TYPE_SUBSCRIPTION_EXPIRED,
  CREDITS_BANNER_LEVEL_FAILURE,
  CREDITS_BANNER_LEVEL_WARNING,
} from "../../shared/creditsBanner";

const getBannerPayload = (banner) => {
  if (banner?.data && typeof banner.data === "object") {
    if ("banner" in banner.data) {
      return banner.data.banner;
    }

    return banner.data;
  }

  if (banner && typeof banner === "object" && "banner" in banner) {
    return banner.banner;
  }

  return banner || null;
};

const getNormalizedType = (payload = {}) =>
  String(payload?.type || "").toUpperCase();

const getBannerVariant = (payload = {}) => {
  const normalizedLevel = String(payload?.level || "").toLowerCase();

  if (normalizedLevel === CREDITS_BANNER_LEVEL_FAILURE) {
    return CREDITS_BANNER_LEVEL_FAILURE;
  }

  return CREDITS_BANNER_LEVEL_WARNING;
};

const getBannerTitle = (payload) => {
  return payload?.message || "";
};

const getBannerDescription = (payload) => {
  return payload?.detail || "";
};

const getBannerButtonLabel = (payload, variant) => {
  const normalizedType = getNormalizedType(payload);

  if (
    normalizedType === "CREDITS_LOW" ||
    normalizedType === CREDIT_ALERT_TYPE_CREDITS_EXHAUSTED ||
    normalizedType === CREDIT_ALERT_TYPE_CREDITS_LOW_90 ||
    normalizedType === CREDIT_ALERT_TYPE_CREDITS_LOW_75
  ) {
    return "Buy More";
  }

  if (normalizedType === CREDIT_ALERT_TYPE_PILOT_EXPIRED) {
    return "Upgrade Plan";
  }

  if (normalizedType === "TRIAL_ENDING_SOON") {
    return "Upgrade Plan";
  }

  if (normalizedType === CREDIT_ALERT_TYPE_SUBSCRIPTION_EXPIRED) {
    return "Upgrade Plan";
  }

  return variant === CREDITS_BANNER_LEVEL_FAILURE
    ? "Retry Payment"
    : "Renew Subscription";
};

const getBannerButtonIcon = (buttonLabel = "") => {
  const normalizedLabel = String(buttonLabel).trim().toLowerCase();

  if (
    normalizedLabel === "upgrade plan" ||
    normalizedLabel === "renew subscription"
  ) {
    return UpgradePlanIcon;
  }

  return BuyMore;
};

const isBannerVisible = (payload) => {
  if (!payload || payload === false) {
    return false;
  }

  return true;
};

const CreditsBanner = ({
  banner,
  onAction,
  hideAction = false,
  allowedAlertTypes,
  actionDisabled = false,
  actionDisabledTooltip = "",
}) => {
  const payload = getBannerPayload(banner);

  if (!isBannerVisible(payload)) {
    return null;
  }

  const variant = getBannerVariant(payload);
  const isFailure = variant === CREDITS_BANNER_LEVEL_FAILURE;
  const normalizedType = getNormalizedType(payload);

  if (allowedAlertTypes && !allowedAlertTypes.includes(normalizedType)) {
    return null;
  }
  const title = getBannerTitle(payload);
  const description = getBannerDescription(payload);
  const buttonLabel = getBannerButtonLabel(payload, variant);
  const showButton = !hideAction && Boolean(buttonLabel);
  const bannerClass = isFailure
    ? "credits-banner-error"
    : "credits-banner-warning";

  const handleClick = () => {
    if (!showButton || actionDisabled) {
      return;
    }

    if (typeof onAction === "function") {
      onAction({ ...payload, buttonText: buttonLabel });
    }
  };

  return (
    <div className={`credits-banner ${bannerClass}`}>
      <div className="credits-banner-content">
        <div
          className={`credits-banner-icon ${
            isFailure
              ? "credits-banner-icon-danger"
              : "credits-banner-icon-warning"
          }`}
        >
          {isFailure ? (
            <CloseCircleOutlined />
          ) : (
            <WarningOutlined />
          )}
        </div>
        <div className="credits-banner-copy">
          <div className="credits-banner-title">{title}</div>
          <div className="credits-banner-description">{description}</div>
        </div>
      </div>
      {showButton ? (
        <Tooltip
          title={actionDisabled ? actionDisabledTooltip : ""}
          placement="bottom"
        >
          <span className="credits-banner-button-wrap">
            <Button
              type="primary"
              className={`credits-banner-button ${
                isFailure
                  ? "credits-banner-button-error"
                  : "credits-banner-button-primary"
              }`}
              onClick={handleClick}
              disabled={actionDisabled}
            >
              <img
                src={getBannerButtonIcon(buttonLabel)}
                alt=""
                className="credits-banner-button-icon"
              />
              {buttonLabel}
            </Button>
          </span>
        </Tooltip>
      ) : null}
    </div>
  );
};

export default CreditsBanner;
